import React, { useState } from 'react';
import { AppConfig } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DEFAULT_CONFIG, VOICE_LABELS, TTS_MODELS, STT_MODELS } from '@/lib/constants';
import { Eye, EyeOff } from 'lucide-react';

interface ConfigFormProps {
  initialConfig?: Partial<AppConfig>;
  onSubmit: (config: AppConfig) => void;
  onCancel: () => void;
  errors?: string[];
}

export function ConfigForm({ initialConfig, onSubmit, onCancel, errors = [] }: ConfigFormProps) {
  const [formData, setFormData] = useState<AppConfig>({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWebsocketUrl, setShowWebsocketUrl] = useState(false);
  const [showWebsocketPath, setShowWebsocketPath] = useState(false);
  const [showVoiceAPIKey, setShowVoiceAPIKey] = useState(false);

  const handleChange = (field: keyof AppConfig, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Connection Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Connection Settings</h3>

        {/* Username and Password - Same Row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <div className="relative">
              <Input
                id="username"
                type={showUsername ? "text" : "password"}
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="Enter username"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowUsername(!showUsername)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showUsername ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Enter password"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* WebSocket URL and Path - Same Row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="websocketUrl">WebSocket URL *</Label>
            <div className="relative">
              <Input
                id="websocketUrl"
                type={showWebsocketUrl ? "text" : "password"}
                value={formData.websocketUrl}
                onChange={(e) => handleChange('websocketUrl', e.target.value)}
                placeholder="https://xora01.x-or.cloud/"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowWebsocketUrl(!showWebsocketUrl)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showWebsocketUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="websocketPath">Socket Path *</Label>
            <div className="relative">
              <Input
                id="websocketPath"
                type={showWebsocketPath ? "text" : "password"}
                value={formData.websocketPath}
                onChange={(e) => handleChange('websocketPath', e.target.value)}
                placeholder="/ws/socket.io"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowWebsocketPath(!showWebsocketPath)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showWebsocketPath ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Voice Settings</h3>

        <div className="space-y-2">
          <Label htmlFor="voiceAPIKey">Voice API Key *</Label>
          <div className="relative">
            <Input
              id="voiceAPIKey"
              type={showVoiceAPIKey ? "text" : "password"}
              value={formData.voiceAPIKey}
              onChange={(e) => handleChange('voiceAPIKey', e.target.value)}
              placeholder="Enter ElevenLabs API key"
              className="pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowVoiceAPIKey(!showVoiceAPIKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showVoiceAPIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ttsModel">Text-to-Speech Model</Label>
          <select
            id="ttsModel"
            value={formData.ttsModel}
            onChange={(e) => handleChange('ttsModel', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Object.entries(TTS_MODELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label>Voice Selection</Label>
          <RadioGroup
            value={formData.voiceGender}
            onValueChange={(value) => handleChange('voiceGender', value)}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="male" id="male" />
              <Label htmlFor="male" className="font-normal cursor-pointer text-xs">{VOICE_LABELS.male}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="female" id="female" />
              <Label htmlFor="female" className="font-normal cursor-pointer text-xs">{VOICE_LABELS.female}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="female2" id="female2" />
              <Label htmlFor="female2" className="font-normal cursor-pointer text-xs">{VOICE_LABELS.female2}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="female3" id="female3" />
              <Label htmlFor="female3" className="font-normal cursor-pointer text-xs">{VOICE_LABELS.female3}</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sttModel">Speech-to-Text Model</Label>
          <select
            id="sttModel"
            value={formData.sttModel}
            onChange={(e) => handleChange('sttModel', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Object.entries(STT_MODELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Options</h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="cameraEnabled"
            checked={formData.cameraEnabled}
            onCheckedChange={(checked) => handleChange('cameraEnabled', checked as boolean)}
          />
          <Label htmlFor="cameraEnabled" className="font-normal cursor-pointer">
            Enable camera
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="autoReconnect"
            checked={formData.autoReconnect}
            onCheckedChange={(checked) => handleChange('autoReconnect', checked as boolean)}
          />
          <Label htmlFor="autoReconnect" className="font-normal cursor-pointer">
            Auto-reconnect on disconnect
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="voiceModeEnabled"
            checked={formData.voiceModeEnabled}
            onCheckedChange={(checked) => handleChange('voiceModeEnabled', checked as boolean)}
          />
          <Label htmlFor="voiceModeEnabled" className="font-normal cursor-pointer">
            Enable voice mode (PTT, STT, TTS)
          </Label>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm font-semibold text-destructive mb-1">Errors:</p>
          <ul className="text-sm text-destructive space-y-1 list-disc list-inside">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Connect
        </Button>
      </div>
    </form>
  );
}
